import { CloseButton, Slider, Text, TextInput } from "@mantine/core";
import { Ban, Check, Search } from "lucide-react";
import { useState } from "react";

import type { TransitionKind } from "../../api/generated/model";
import { ParamSection } from "./params";
import { useTransitionPreview } from "./hover-previews";
import {
  MIN_TRANSITION_MS,
  type Junction,
  type Transition,
  type TimelineEdit,
} from "./timeline";
import {
  TRANSITIONS,
  TRANSITION_CATEGORIES,
  searchTransitions,
  type TransitionCategoryId,
  type TransitionInfo,
} from "./transition-catalog";
import classes from "./transitions.module.css";

/** Live's Duration slider runs to a quarter of the shorter picture (its 0.25), by 0.1 s. */
const SLIDER_SHARE = 0.25;
const SLIDER_STEP_MS = 100;

/** Live's Duration as it writes it: "2.0s". */
function durationText(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Live's Transition Settings (#240), for the chosen transition: "Filter transitions...", the
 * category pills, a grid of three to a row whose first card, "None", takes the transition away,
 * a check on the current one and live's "Custom" badge on its own shaders; and a Duration slider
 * under them from 0.1 s to a quarter of the shorter picture.
 */
export function TransitionSettings(props: {
  transition: Transition;
  junction: Junction;
  onEdit: (edit: TimelineEdit) => void;
}) {
  const { transition, junction, onEdit } = props;
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<TransitionCategoryId | "all">("all");
  const listed = searchTransitions(
    category === "all" ? TRANSITIONS : TRANSITIONS.filter((each) => each.category === category),
    query,
  );
  const choose = (kind: TransitionKind) =>
    onEdit({ type: "setTransition", id: transition.id, fromId: transition.fromId, toId: transition.toId, kind });
  return (
    <ParamSection title="Transition Settings">
      <section className={classes.settings} aria-label="Transition settings">
        <TextInput
          classNames={{ input: classes.filterInput }}
          placeholder="Filter transitions..."
          aria-label="Filter transitions"
          leftSection={<Search size={14} aria-hidden />}
          rightSection={
            query === "" ? null : (
              <CloseButton size="xs" aria-label="Clear the filter" onClick={() => setQuery("")} />
            )
          }
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        <div className={classes.pills}>
          {[{ id: "all" as const, label: "All" }, ...TRANSITION_CATEGORIES].map((each) => (
            <button
              key={each.id}
              type="button"
              className={classes.pill}
              aria-pressed={category === each.id}
              onClick={() => setCategory(each.id)}
            >
              {each.label}
            </button>
          ))}
        </div>
        <ul className={classes.choices} aria-label="Transitions">
          <li>
            <button
              type="button"
              className={classes.choice}
              aria-pressed={false}
              aria-label="None"
              onClick={() => onEdit({ type: "removeTransition", id: transition.id })}
            >
              <span className={classes.choiceCard} data-none="true" aria-hidden>
                <Ban size={24} />
              </span>
              <span className={classes.choiceName}>None</span>
            </button>
          </li>
          {listed.map((info) => (
            <li key={info.key}>
              <Choice info={info} current={info.kind === transition.kind} onChoose={choose} />
            </li>
          ))}
        </ul>
        <div className={classes.duration}>
          <Text className={classes.durationLabel}>Duration</Text>
          <div className={classes.durationField}>
            <Slider
              className={classes.durationSlider}
              min={MIN_TRANSITION_MS}
              max={Math.max(MIN_TRANSITION_MS, junction.limitMs * SLIDER_SHARE)}
              step={SLIDER_STEP_MS}
              value={transition.durationMs}
              label={null}
              thumbLabel="Duration"
              thumbProps={{ "aria-valuetext": `${(transition.durationMs / 1000).toFixed(1)} seconds` }}
              onChange={(durationMs) =>
                onEdit({ type: "setTransitionDuration", id: transition.id, durationMs })
              }
            />
            <Text className={classes.durationValue}>{durationText(transition.durationMs)}</Text>
          </div>
        </div>
      </section>
    </ParamSection>
  );
}

function Choice(props: {
  info: TransitionInfo;
  current: boolean;
  onChoose: (kind: TransitionKind) => void;
}) {
  const { info, current, onChoose } = props;
  // Live's card plays its preview as soon as the pointer is on it.
  const { slot, hover } = useTransitionPreview(info.kind, 0);
  const face = (
    <>
      <span className={classes.choiceCard} aria-hidden>
        <span className={classes.choiceCardName}>{info.name}</span>
        <span ref={slot} className={classes.preview} />
        {info.custom ? <span className={classes.custom}>Custom</span> : null}
        {current ? (
          <span className={classes.check}>
            <Check size={12} strokeWidth={3} />
          </span>
        ) : null}
      </span>
      <span className={classes.choiceName}>{info.name}</span>
    </>
  );
  const { kind } = info;
  return (
    <button
      type="button"
      className={classes.choice}
      aria-pressed={current}
      aria-label={info.name}
      onClick={() => onChoose(kind)}
      {...hover}
    >
      {face}
    </button>
  );
}
